import { ProductRelationshipProductPair } from 'src/app/models/interfaces/ProductRelationshipProductPair';
import { AttributeSettingsDTO } from '../../../DTOs/AttributeSettingsDTO';
import { Attribute } from '../../Attribute';
import { AttributeValue, MaterialValue, ModelValue } from '../../AttributeValue';
import { AttributePair, AttributeSetting } from '../../Product';
import { ProductBlueprint } from '../../ProductBlueprint';
import { AttributeValueInstance, AttributeValueOrNumber } from '../AttributeValueInstances/AttributeValueInstance';
import { MaterialValueInstance } from '../AttributeValueInstances/MaterialValuerInstance';
import { ModelValueInstance } from '../AttributeValueInstances/ModelValueInstance';
import { NumericValueInstance } from '../AttributeValueInstances/NumericValueInstance';
import { AttributeWithAttributeChildren } from '../AttributeSettingsController';

export class AttributeInstance<T extends Attribute> {
  private static createAttributeValueInstanceBasedOnAttributeValueType(attributeValue: AttributeValueOrNumber) {
    if (typeof attributeValue === 'number') {
      return new NumericValueInstance(attributeValue);
    }
    if (attributeValue instanceof MaterialValue) {
      return new MaterialValueInstance(attributeValue);
    }
    if (attributeValue instanceof ModelValue) {
      return new ModelValueInstance(attributeValue);
    }
    return new AttributeValueInstance<AttributeValue>(attributeValue);
  }

  protected _attribute: T;
  protected _setAttributeValue?: AttributeValueInstance<AttributeValueOrNumber>;

  protected _isApplied: boolean;

  protected priority?: number;

  constructor(attribute: T) {
    this._isApplied = true;
    this._attribute = attribute;
  }

  public get attribute() {
    return this._attribute;
  }

  public get attributeValue() {
    return this._setAttributeValue?.attributeValue;
  }

  public get isApplied() {
    return this._isApplied;
  }

  //če vrne observable, pomeni da je uspešno nastavil atribut, sicer pa ga ni.
  public setAttributeValue(attributeValue: AttributeValueOrNumber, priority: number) {
    if (attributeValue instanceof AttributeValue && attributeValue !== this._setAttributeValue?.attributeValue) {
      if (this.priority && priority < this.priority) return undefined;
      if (this._setAttributeValue) {
        this._setAttributeValue.dispose();
      }
      this.priority = priority;
      this._setAttributeValue = AttributeInstance.createAttributeValueInstanceBasedOnAttributeValueType(attributeValue);
      this._setAttributeValue.setDefaultAttributes([]);
      return this._setAttributeValue?.attributeWasSet$ ? this._setAttributeValue?.attributeWasSet$ : true;
    }
    return undefined;
  }

  public setAttributeValueAsApplied(attribute: Attribute) {
    if (this._attribute === attribute) {
      this._isApplied = true;
      return;
    }
    if (this._setAttributeValue) return this._setAttributeValue.setAttributeValueAsApplied(attribute);
  }

  public checkIfAttributesAreApplied() {
    let attributeValueIsApplied = true;
    if (this._setAttributeValue) {
      attributeValueIsApplied = this._setAttributeValue.checkIfAttributesAreApplied();
      if (!attributeValueIsApplied) return false;
    }
    return this._isApplied;
  }

  /* public setAttributeChildren(attribute: Attribute, products: ProductRelationshipProductPair[]) {
    if (this._setAttributeValue) {
      this._setAttributeValue.setAttributeChildren(attribute, products);
    }
  }

  public getAllAttributeChildren(attributeWithAttributeChildren: AttributeWithAttributeChildren[]) {
    this._setAttributeValue?.getAllAttributeChildren(attributeWithAttributeChildren);
  } */

  public generateAttributeSettingsDTO(returnArray: AttributeSettingsDTO[]) {
    returnArray.push(
      new AttributeSettingsDTO(
        Number(this._attribute.id),
        this._setAttributeValue?.attributeValueId ? Number(this._setAttributeValue?.attributeValueId) : undefined,
        typeof this._setAttributeValue?.attributeValue === 'number'
          ? (this._setAttributeValue.attributeValue as number)
          : undefined,
        undefined,
        this.priority,
      ),
    );
    this._setAttributeValue?.generateAttributeSettingsDTO(returnArray);
  }

  public setDefaultAttributeValues(overrideAttributes: AttributePair[]) {
    if (this._setAttributeValue) {
      this._setAttributeValue.setDefaultAttributes(overrideAttributes);
    }
  }

  public setAttribute(attribute: Attribute, attributeValue: AttributeValueOrNumber, priority: number) {
    if (this._setAttributeValue) {
      return this._setAttributeValue.setAttribute(attribute, attributeValue, priority);
    }
    return false;
  }

  public reApplyAttributes() {
    this._setAttributeValue?.reApplyAttributes();
  }

  /* public getAttributeChildren(attribute: Attribute, attributeChildren: ProductRelationshipProductPair[]) {
    this._setAttributeValue?.getAttributeChildren(attribute, attributeChildren);
  } */

  public generateAttributePairs(attributePairs: AttributePair[]) {
    if (this._setAttributeValue) {
      attributePairs.push({ attribute: this._attribute, attributeValue: this._setAttributeValue?.attributeValue });
    }
    this._setAttributeValue?.generateAttributePairs(attributePairs);
  }

  public getAttributeValueFromAttribute(attribute: Attribute) {
    return this._setAttributeValue?.getAttributeValueFromAttribute(attribute);
  }

  public referenceAttributesCanBeAppliedToProductBlueprint(productBlueprint: ProductBlueprint) {
    if (!this._setAttributeValue) return false;
    return this._setAttributeValue.referenceAttributesCanBeAppliedToProductBlueprint(productBlueprint);
  }

  public findGlobalAttributes(attributeInstances: AttributeInstance<Attribute>[]) {
    if (this.attribute.isGlobal) {
      attributeInstances.push(this);
    }
    return this._setAttributeValue?.findGlobalAttributes(attributeInstances);
  }

  public generateAttributeSetting() {
    const attributeSetting: AttributeSetting | undefined = this._setAttributeValue
      ? ({
          attributeValue: this._setAttributeValue.attributeValue,
          isApplied: this._isApplied,
          map: this._setAttributeValue.generateAttributeMap(),
        } as AttributeSetting)
      : undefined;
    return attributeSetting;
  }
}
